import { useCallback, useState } from "react";

import { defaultColumnWidth } from "./companyColumns";

/**
 * Column widths somebody has dragged, remembered per list.
 *
 * In localStorage rather than on the server, unlike *which* columns a list
 * shows. The set of columns is a decision a manager makes for the desk; how
 * wide they are is a decision about the screen you happen to be sitting at, and
 * pushing one person's 34" monitor onto everybody else's laptop would be worse
 * than the default we started from. It also means a drag costs no request.
 *
 * Widths are stored by column key, not by position, so re-ordering or adding a
 * column leaves the rest where the person put them.
 */
const KEY = "crm.columnWidths";

/** Narrow enough to lose the heading, wide enough to swallow the table. */
export const MIN_WIDTH = 56;
export const MAX_WIDTH = 600;

type Store = Record<string, Record<string, number>>;

export function clampWidth(px: number): number {
  return Math.round(Math.min(Math.max(px, MIN_WIDTH), MAX_WIDTH));
}

function read(): Store {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Store) : {};
  } catch {
    // Safari private mode throws, and a stale entry could be anything. A
    // column layout is never worth breaking the list over.
    return {};
  }
}

export function useColumnWidths(list: string): {
  /** What this column should be, dragged or default. */
  widthOf: (key: string) => number;
  /** Remember a drag. Called once it finishes, not per pixel. */
  setWidth: (key: string, px: number) => void;
  /** Put one column back to its default -- the double-click on the handle. */
  resetWidth: (key: string) => void;
} {
  const [widths, setWidths] = useState<Record<string, number>>(
    () => read()[list] ?? {},
  );

  const save = useCallback(
    (next: Record<string, number>): void => {
      setWidths(next);
      try {
        const store = read();
        window.localStorage.setItem(
          KEY,
          JSON.stringify({ ...store, [list]: next }),
        );
      } catch {
        // Unwritable storage costs this person the widths next time they load,
        // which is not a reason to lose the drag they just made.
      }
    },
    [list],
  );

  return {
    widthOf: (key) => widths[key] ?? defaultColumnWidth(key),
    setWidth: (key, px) => save({ ...widths, [key]: clampWidth(px) }),
    resetWidth: (key) => {
      const next = { ...widths };
      delete next[key];
      save(next);
    },
  };
}
